import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Sparkles, TrendingUp } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { HUBS, WOMEN_HUB, hubByKey, applyHubGlow, fmtNum } from "@/lib/hubs";
import { useT } from "@/lib/i18n";
import { TrackRow, TrackCard } from "@/components/TrackItems";
import { ArtistBadge } from "@/components/ArtistBadge";
import { Skeleton } from "@/components/ui/skeleton";
import { CollectionCard } from "@/components/Collections";

const EqIcon = ({ color }) => (
  <span data-testid="hub-tile-eq-icon" className="flex items-end gap-[3px] h-7 opacity-80" aria-hidden>
    {[0.55, 0.85, 1, 0.7].map((h, j) => <span key={j} className="w-[4px] rounded-[1px] group-hover:eq-bar" style={{ height: `${h * 100}%`, background: color, animationDelay: `${j * 130}ms` }} />)}
  </span>
);

const TileArt = ({ hub, scale = "scale-[1.08]" }) => (
  <>
    <img src={hub.art} alt="" loading="lazy" className={`absolute inset-0 w-full h-full object-cover ${scale} group-hover:scale-[1.16] transition-transform duration-700 ease-out`} />
    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-black/5" />
    <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" style={{ boxShadow: `inset 0 0 0 1px ${hub.color}99, inset 0 -50px 70px -40px ${hub.glow}` }} />
  </>
);

const tileBase = "reveal group relative text-left rounded-2xl overflow-hidden border border-white/15 bg-[#12121A] h-44 lg:h-[13rem] hover:scale-[1.03] transition-[transform,box-shadow] duration-300";

const HubTile = ({ hub, count, onClick, i }) => (
  <button data-testid={`genre-hub-tile-${hub.key}`} onClick={onClick} style={{ animationDelay: `${i * 40}ms`, "--tile-glow": hub.color }}
    className={`${tileBase} hover:shadow-[0_16px_44px_-16px_var(--tile-glow)]`}>
    <TileArt hub={hub} />
    <span className="absolute top-4 right-4"><EqIcon color={hub.color} /></span>
    <div className="absolute inset-x-0 bottom-0 p-4">
      <div className="font-display text-xl lg:text-2xl font-extrabold leading-[1.1] text-white [text-shadow:0_2px_14px_rgba(0,0,0,0.65)] line-clamp-2">{hub.name}</div>
      <div className="text-xs text-white/75 mt-1 truncate">{hub.blurb}</div>
      <div data-testid={`hub-tile-count-${hub.key}`} className="text-[10px] font-mono font-semibold uppercase tracking-[0.18em] mt-2" style={{ color: hub.color }}>{count ?? "–"} tracks</div>
    </div>
  </button>
);

const WomenTile = ({ count, onClick }) => (
  <button data-testid="genre-hub-tile-100-women-in-music" onClick={onClick}
    className={`${tileBase} col-span-2 hover:shadow-[0_18px_54px_-16px_#FF4D8D]`}>
    <TileArt hub={WOMEN_HUB} scale="scale-[1.05]" />
    <div className="absolute inset-0 bg-gradient-to-r from-black/45 via-black/15 to-transparent" />
    <div className="relative h-full flex flex-col p-5 lg:p-6">
      <span className="self-start text-[9px] sm:text-[10px] font-mono font-semibold uppercase tracking-[0.22em] text-white bg-black/55 backdrop-blur-md border border-white/20 px-3 py-1.5 rounded-full">Spotlight Hub · Editorial</span>
      <div className="mt-auto">
        <div className="font-display text-3xl lg:text-[2.6rem] font-black leading-[1] text-white [text-shadow:0_4px_24px_rgba(0,0,0,0.5)]">100% Women in Music</div>
        <p className="text-sm text-white/85 mt-2 line-clamp-1">{WOMEN_HUB.blurb}</p>
        <div data-testid="women-hub-enter" className="mt-2 inline-flex items-center gap-2 text-sm text-white/90">Enter the hub <ArrowUpRight size={15} /> <span>{count ?? "–"} tracks</span></div>
      </div>
    </div>
  </button>
);

const Section = ({ title, icon: Icon, children, testId }) => (
  <section data-testid={testId} className="mt-12">
    <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2 mb-5">{Icon && <Icon size={22} className="text-[var(--hub)]" />}{title}</h2>
    {children}
  </section>
);

const ArtistChip = ({ a }) => (
  <Link to={`/artist/${a.slug}`} data-testid={`spotlight-artist-${a.slug}`} className="group flex flex-col items-center text-center w-32 shrink-0">
    <div className="w-28 h-28 rounded-full overflow-hidden ring-2 ring-white/10 group-hover:ring-[var(--hub)] transition-[box-shadow] duration-300">
      <img src={mediaUrl(a.avatar)} alt={a.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
    </div>
    <div className="mt-3 text-sm font-semibold flex items-center gap-1">{a.name} <ArtistBadge badge={a.badge} /></div>
    <div className="text-xs font-mono text-zinc-500">{a.growth_pct != null ? `+${a.growth_pct}% · 7d` : `${fmtNum(a.total_streams)} streams`}</div>
  </Link>
);

const MomentArtist = ({ a, i, label }) => (
  <Link to={`/artist/${a.slug}`} data-testid={`moment-artist-${a.slug}`} style={{ animationDelay: `${i * 35}ms` }}
    className="reveal group snap-start shrink-0 w-36 sm:w-40 p-3 rounded-2xl border border-white/[0.07] bg-[#12121A] hover:border-[var(--hub)] hover:-translate-y-1 transition-[transform,border-color] duration-300">
    <div className="aspect-square rounded-full overflow-hidden ring-2 ring-white/10 group-hover:ring-[var(--hub)] transition-[box-shadow] duration-300 bg-zinc-800 flex items-center justify-center">
      {a.avatar ? <img src={mediaUrl(a.avatar)} alt={a.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
        : <span className="font-display text-4xl font-black text-zinc-500">{(a.name || "?").slice(0, 1).toUpperCase()}</span>}
    </div>
    <div className="mt-3 text-sm font-semibold flex items-center justify-center gap-1 text-center"><span className="truncate">{a.name}</span> <ArtistBadge badge={a.badge} /></div>
    <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 text-center mt-0.5">{a.track_count || 0} {label}</div>
  </Link>
);

const ArtistsOfTheMoment = () => {
  const t = useT();
  const [list, setList] = useState(null);
  useEffect(() => { api.get("/artists-moment").then((r) => setList(r.data)).catch(() => setList([])); }, []);
  if (!list || list.length === 0) return null;
  return (
    <Section title="Artists of the Moment" icon={Sparkles} testId="artists-of-the-moment">
      <div className="flex gap-4 overflow-x-auto snap-x pb-3 -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-none">
        {list.map((a, i) => <MomentArtist key={a.user_id} a={a} i={i} label={t("tracks")} />)}
      </div>
    </Section>
  );
};

const HubView = ({ hubKey, onBack }) => {
  const t = useT();
  const hub = hubByKey(hubKey);
  const [d, setD] = useState(null);
  useEffect(() => {
    setD(null);
    api.get(`/hubs/${hubKey}`).then((r) => setD(r.data));
  }, [hubKey]);
  return (
    <div data-testid={`hub-view-${hubKey}`} className="reveal">
      <button data-testid="hub-back-button" onClick={onBack} className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white mb-6"><ArrowLeft size={16} /> {t("backToHubs")}</button>
      <div className="eyebrow" style={{ color: hub.color }}>Hub · {hubKey === "women" ? "Editorial showcase" : "Genre"}</div>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-none mt-2" data-testid="hub-view-title">{hub.name}</h1>
      <p className="text-zinc-400 mt-3 max-w-xl">{hub.blurb}</p>
      {hubKey === "women" && <p className="text-xs text-zinc-500 mt-2 max-w-2xl">Editorial channel based on artist self-attestation at upload. It never restricts access to core services, distribution or monetization for anyone.</p>}
      {!d ? <div className="mt-10 space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 bg-white/5" />)}</div> : (
        <div className="grid lg:grid-cols-[1fr_340px] gap-10">
          <div>
            <Section title={t("topCharts")} icon={TrendingUp} testId="hub-top-charts">
              <div className="surface p-2">{d.top_tracks.map((tr, i) => <TrackRow key={tr.track_id} track={tr} list={d.top_tracks} rank={i + 1} />)}</div>
            </Section>
            <Section title={t("trending")} testId="hub-trending">
              <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-5">{d.trending.map((tr) => <TrackCard key={tr.track_id} track={tr} list={d.trending} />)}</div>
            </Section>
          </div>
          <aside>
            <Section title={t("spotlight")} icon={Sparkles} testId="hub-spotlight">
              <div className="grid grid-cols-2 gap-6">{d.spotlight.map((a) => <ArtistChip key={a.user_id} a={a} />)}</div>
            </Section>
            {d.rising.length > 0 && (
              <Section title={t("rising")} testId="hub-rising">
                <div className="grid grid-cols-2 gap-6">{d.rising.map((a) => <ArtistChip key={a.user_id} a={a} />)}</div>
              </Section>
            )}
          </aside>
        </div>
      )}
    </div>
  );
};

export default function Home() {
  const t = useT();
  const [params, setParams] = useSearchParams();
  const active = params.get("hub");
  const [counts, setCounts] = useState({});
  const [fresh, setFresh] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  useEffect(() => {
    api.get("/hubs").then((r) => setCounts(Object.fromEntries(r.data.map((h) => [h.key, h.track_count]))));
    api.get("/tracks", { params: { sort: "new", limit: 12 } }).then((r) => setFresh(r.data));
    api.get("/playlists", { params: { limit: 6 } }).then((r) => setPlaylists(r.data));
  }, []);
  useEffect(() => {
    applyHubGlow(active ? hubByKey(active) : null);
    sessionStorage.setItem("sonora_src", active ? "hub_tiles" : "direct");
    window.scrollTo({ top: 0, behavior: "smooth" });
    return () => applyHubGlow(null);
  }, [active]);
  const open = (k) => setParams({ hub: k });
  if (active && hubByKey(active)) return <HubView hubKey={active} onBack={() => setParams({})} />;
  return (
    <div data-testid="home-page">
      <div className="reveal max-w-3xl">
        <div className="eyebrow">Soundra · {t("hubs")}</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[0.95] mt-3">{t("heroTitle")} <span className="text-[var(--hub)]">{t("heroAccent")}</span></h1>
        <p className="text-zinc-400 mt-4 text-base">{t("heroSub")}</p>
      </div>
      <div data-testid="genre-hub-grid" className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 lg:gap-4 mt-10">
        <WomenTile count={counts.women} onClick={() => open("women")} />
        {HUBS.map((h, i) => <HubTile key={h.key} hub={h} i={i} count={counts[h.key]} onClick={() => open(h.key)} />)}
      </div>
      <ArtistsOfTheMoment />
      {playlists.length > 0 && (
        <Section title="Featured playlists" testId="featured-playlists">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-5">{playlists.map((p) => <CollectionCard key={p.playlist_id} item={p} kind="playlist" />)}</div>
        </Section>
      )}
      <Section title={t("freshDrops")} testId="fresh-drops">
        <div className="surface p-2">{fresh.map((tr) => <TrackRow key={tr.track_id} track={tr} list={fresh} />)}</div>
      </Section>
    </div>
  );
}
